class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        // CSC (Goal C): land focus on the close (X) button on open, not the
        // dialog container, so keyboard/SR users start at a known control.
        const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // CSC fix: clear `is-empty` off the <cart-drawer> element itself on every
    // render. We're rendering cart contents after an add, so the cart is never
    // empty here. Stock Dawn only checks `.drawer__inner` (which never carries
    // the class) and relies on product-form.js's finally-block to strip it —
    // so adds via csc-pdp-cart.js / cscCrossSellAdd left `is-empty` stuck on,
    // which hid the header, forced the empty grid layout (padding:0 → clipped
    // "GBP"), and collapsed the line-item thumbnail to 0×0.
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    // CSC fix 2026-08-28: `parsedState.sections` is NOT guaranteed to be present.
    // csc-pdp-cart.js and the cross-sell handlers decide whether to REQUEST
    // sections at click time (`typeof cartEl.getSectionsToRender === 'function'`)
    // but decide whether to RENDER them at response time (`typeof
    // cartEl.renderContents === 'function'`). csc-pdp-cart.js is a blocking
    // script while cart-drawer.js is deferred, so there is a window where this
    // element is in the DOM but not yet upgraded: a click in that window sends
    // no `sections`, and by the time the response lands the upgrade has run and
    // renderContents is called with a section-less payload. Third-party scripts
    // on the page (Shopify's shop-cart-sync module) can also call this with
    // their own payload.
    //
    // Stock Dawn dereferenced parsedState.sections[...] unguarded, so that threw
    // a raw TypeError which the PDP handlers printed VERBATIM into the customer-
    // facing error box under Add To Basket ("null is not an object (evaluating
    // 'parsedState.sections[section.id]')") — the item was in the basket but the
    // customer saw an error and abandoned. Recover by fetching the sections
    // instead of crashing; only redirect to /cart if even that fails.
    const sections = parsedState && parsedState.sections;
    if (!sections) {
      this.refreshSectionsFromServer(parsedState);
      return;
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      // A requested-but-unrenderable section comes back as null (verified against
      // the live cart API) — skip it rather than feeding null to the DOMParser.
      const sectionHtml = sections[section.id];
      if (!sectionHtml) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // Re-fetch the sections this drawer needs, then render normally. Used when a
  // caller hands us a payload with no `sections` (see renderContents above).
  refreshSectionsFromServer(parsedState) {
    const ids = this.getSectionsToRender()
      .map((section) => section.id)
      .join(',');

    fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('sections fetch failed'))))
      .then((sections) => {
        if (!sections) throw new Error('sections fetch returned nothing');
        // Recurses exactly once: `sections` is truthy on this path.
        this.renderContents({ id: parsedState && parsedState.id, sections: sections });
      })
      .catch(() => {
        // Last resort: send the customer to the cart page so they can still see
        // the item they just added and check out.
        window.location.href = '/cart';
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
